/**
 * @file password.ts
 * @description Os requisitos de senha, num lugar só.
 *
 * ## Por que existe
 *
 * A regra estava duplicada em dois arquivos de front, com os mesmos quatro
 * regexes escritos à mão (`RegisterPasswordScreen` no app e
 * `ForcePasswordChangeModal` no painel) — e em nenhum lugar do servidor. A API
 * aceitava `min(8)` em toda rota que cria ou troca senha.
 *
 * Duas consequências:
 *
 *   1. Uma tela nova nasce sem a regra por esquecimento, que foi o que
 *      aconteceu com o aceite de convite: pedia 8 caracteres e mais nada,
 *      enquanto o cadastro de paciente ao lado exigia símbolo e maiúscula.
 *   2. A regra é contornável por API. Ela nunca foi uma exigência do sistema;
 *      era uma sugestão de dois formulários.
 *
 * Aqui a lista é uma só e o servidor pode aplicá-la.
 *
 * ## Duas regras (A085)
 *
 * - **Equipe** (quem vê dado de saúde de outras pessoas): os quatro
 *   requisitos abaixo — maiúscula, número, símbolo, 8 caracteres.
 * - **Paciente**: 8 caracteres e nada de senha comum ou previsível. A
 *   recomendação atual (NIST SP 800-63B) desaconselha exigir tipos de
 *   caractere — empurra para `Senha@123` e para a senha anotada — e manda
 *   barrar as senhas mais usadas. O cadastro novo (A062) já pedia só os 8
 *   caracteres; faltava barrar as comuns e aplicar no servidor.
 *
 * Nas duas, a senha comum é recusada.
 */

export interface RequisitoDeSenha {
  chave: 'tamanho' | 'maiuscula' | 'numero' | 'simbolo';
  rotulo: string;
  atende: (senha: string) => boolean;
}

export const TAMANHO_MINIMO_DE_SENHA = 8;

/** Na ordem em que a tela lista. */
export const REQUISITOS_DE_SENHA: RequisitoDeSenha[] = [
  {
    chave: 'maiuscula',
    rotulo: 'Pelo menos uma letra maiúscula',
    atende: (s) => /[A-Z]/.test(s),
  },
  {
    chave: 'numero',
    rotulo: 'Pelo menos um número',
    atende: (s) => /\d/.test(s),
  },
  {
    chave: 'simbolo',
    rotulo: 'Pelo menos um símbolo',
    // Qualquer caractere que não seja letra, número ou espaço. A lista fixa
    // de antes deixava de fora hífen, sublinhado, `+`, `=`…
    atende: (s) => /[^A-Za-zÀ-ÖØ-öø-ÿ0-9\s]/.test(s),
  },
  {
    chave: 'tamanho',
    rotulo: `Mínimo de ${TAMANHO_MINIMO_DE_SENHA} caracteres`,
    atende: (s) => s.length >= TAMANHO_MINIMO_DE_SENHA,
  },
];

/** Quais requisitos a senha atende — para a tela pintar a lista. */
export function conferirSenha(senha: string): Record<RequisitoDeSenha['chave'], boolean> {
  return Object.fromEntries(REQUISITOS_DE_SENHA.map((r) => [r.chave, r.atende(senha)])) as Record<
    RequisitoDeSenha['chave'],
    boolean
  >;
}

export function senhaEhForte(senha: string): boolean {
  return REQUISITOS_DE_SENHA.every((r) => r.atende(senha));
}

/**
 * O que falta, em uma frase — para o erro do servidor dizer o que corrigir em
 * vez de só recusar.
 */
export function oQueFaltaNaSenha(senha: string): string | null {
  const faltando = REQUISITOS_DE_SENHA.filter((r) => !r.atende(senha)).map((r) => r.rotulo.toLowerCase());
  if (faltando.length === 0) return null;
  return `A senha precisa de: ${faltando.join(', ')}.`;
}

// --- Senha comum ou previsível (A085) -----------------------------------------

/**
 * As senhas mais usadas — as listas públicas de vazamentos, com as do Brasil.
 * Comparadas em minúsculas.
 */
const SENHAS_COMUNS = new Set([
  '12345678', '123456789', '1234567890', '12341234', '11223344', '00000000', '11111111', '22222222', '88888888',
  '87654321', '98765432', '01234567', '123123123', '147258369', '159753456', '12344321', '13579246', '102030405060',
  'password', 'password1', 'password123', 'passw0rd', 'qwertyui', 'qwerty123', 'qwertyuiop', 'asdfghjk', 'zxcvbnm1',
  'abcdefgh', 'abcd1234', 'abc12345', 'a1b2c3d4', 'iloveyou', 'sunshine', 'princess', 'football', 'baseball',
  'welcome1', 'trustno1', 'superman', 'whatever', 'starwars', 'dragon12', 'letmein1', 'master12', 'computer',
  'senha123', 'senha1234', 'senha12345', 'minhasenha', 'mudar123', 'trocar123', 'mudarsenha', 'teste123', 'teste1234',
  'brasil123', 'brasil2024', 'brasil2025', 'brasil2026', 'flamengo', 'corinthians', 'palmeiras', 'saopaulo',
  'vasco123', 'gremio123', 'cruzeiro', 'botafogo', 'fluminense', 'santos123', 'internacional',
  'jesus123', 'jesuscristo', 'deusefiel', 'deus1234', 'familia1', 'amorzinho', 'meuamor1', 'eueteamo', 'teamo123',
  'mamae123', 'papai123', 'mudar@123', 'senha@123', 'admin123', 'administrador', 'eva12345', 'evasaude',
]);

/** Palavras que, com números ou símbolos em volta, não fazem uma senha. */
const PALAVRAS_PREVISIVEIS = new Set([
  'senha', 'password', 'pass', 'teste', 'test', 'admin', 'mudar', 'trocar', 'brasil', 'eva', 'evasaude', 'amor',
  'deus', 'jesus', 'familia', 'mamae', 'papai', 'bebe', 'qwerty', 'abc', 'abcd', 'gestante', 'gravida',
]);

const SEQUENCIAS = ['0123456789', 'abcdefghijklmnopqrstuvwxyz', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

function ehSequenciaOuRepeticao(s: string): boolean {
  if (/^(.)\1+$/.test(s)) return true; // aaaaaaaa, 11111111
  if (/^(..)\1+$/.test(s) || /^(...)\1+$/.test(s)) return true; // abababab, 123123123
  return SEQUENCIAS.some((seq) => seq.includes(s) || seq.split('').reverse().join('').includes(s));
}

/**
 * Por que esta senha é fraca demais para qualquer conta — ou `null`. Vale
 * para paciente e para equipe.
 */
export function problemaDeSenhaComum(senha: string, contexto: { email?: string | null } = {}): string | null {
  const minuscula = senha.toLowerCase();
  if (SENHAS_COMUNS.has(minuscula) || ehSequenciaOuRepeticao(minuscula)) {
    return 'Essa senha é muito comum e fácil de adivinhar. Escolha outra.';
  }
  // Palavra previsível com números ou símbolos em volta: "Senha@123", "eva2026".
  const letras = minuscula.replace(/[^a-zà-ÿ]/g, '');
  if (letras && PALAVRAS_PREVISIVEIS.has(letras)) {
    return 'Essa senha é muito comum e fácil de adivinhar. Escolha outra.';
  }
  const usuario = (contexto.email ?? '').toLowerCase().split('@')[0];
  if (usuario.length >= 4 && minuscula.includes(usuario)) {
    return 'Não use o seu e-mail na senha.';
  }
  return null;
}

/** A regra de paciente: 8 caracteres e nada de senha comum. `null` se serve. */
export function problemaNaSenhaDePaciente(senha: string, contexto: { email?: string | null } = {}): string | null {
  if (senha.length < TAMANHO_MINIMO_DE_SENHA) return `A senha precisa de pelo menos ${TAMANHO_MINIMO_DE_SENHA} caracteres.`;
  return problemaDeSenhaComum(senha, contexto);
}

/** A regra da equipe: os quatro requisitos e nada de senha comum. `null` se serve. */
export function problemaNaSenhaDaEquipe(senha: string, contexto: { email?: string | null } = {}): string | null {
  return oQueFaltaNaSenha(senha) ?? problemaDeSenhaComum(senha, contexto);
}
